import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    createMyFoldersAPI,
    deleteMyFoldersAPI,
    getAllMyFoldersAPI,
    updateMyFoldersAPI,
} from '../../../utils/apiServices/myFoldersAPI';
import { CircleSpinner, Delete, Edit, FolderIcon, IconPlus } from '../../../assets/svg';
import { LOCAL_STORAGE, MY_DOCUMENTS, MY_FOLDERS, QUERY_KEYS, ROUTES } from '../../../constants';
import { AdminPageHeader } from '../../Admin';
import { usePagination } from '../../../hooks';
import { getLocalStorageItem, popup } from '../../../utils';
import { EditFileNameModal } from '../../shared/Modal/EditFileNameModal';
import { DeleteConfirmationModal } from '../../shared';
import useWindowSize from '../../../hooks/useWindowSize';
import DeletedDocumentsModal from './DeletedDocumentsModal';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

const INITIAL_STATE = { name: '', id: null, document_count: 0 };

export const FoldersView = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const accountId = getLocalStorageItem(LOCAL_STORAGE.CUSTOMER_ID);
    const [response, setResponse] = useState({ data: [], count: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [currentFolder, setCurrentFolder] = useState(INITIAL_STATE);
    const [folderToDelete, setFolderToDelete] = useState(INITIAL_STATE);
    const pagination = usePagination(response.data.length, response.count);
    const [search, setSearch] = useState('');
    const userId = getLocalStorageItem(LOCAL_STORAGE.USER_ID);
    const { width } = useWindowSize();

    const [debounceSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isFetched, isError, error } = useQuery({
        queryKey: [QUERY_KEYS.FOLDERS, accountId, debounceSearch],
        queryFn: async () =>
            await getAllMyFoldersAPI({
                account_id: accountId,
                search: debounceSearch,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
        placeholderData: { data: [], count: 0 },
        enabled: !!accountId,
    });

    useEffect(() => {
        if (data.data && isFetched) {
            setResponse({ data: data.data, count: data.count });
        }
    }, [data, isFetched]);

    if (isError) popup('error', error.message);

    const createMutation = useMutation({
        mutationFn: async (data) => await createMyFoldersAPI({ body: data }),
        onSuccess: (response) => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.FOLDERS, accountId],
            });
            setShowAddModal(false);
            popup('success', response?.message);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const createFolderHandler = ({ fileName }) => {
        const data = [
            {
                name: fileName,
                account_id: accountId,
                user_id: userId,
            },
        ];
        createMutation.mutate(data);
    };

    const renameMutation = useMutation({
        mutationFn: async (data) => await updateMyFoldersAPI({ data, id: currentFolder?.id }),
        onSuccess: (response) => {
            setCurrentFolder(INITIAL_STATE);
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.FOLDERS, accountId],
            });
            setShowAddModal(false);
            popup('success', response?.message);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const renameFolderHandler = ({ fileName }) => {
        const data = { name: fileName };
        renameMutation.mutate(data);
    };

    const deleteMutation = useMutation({
        mutationFn: async (selectedItemId) =>
            await deleteMyFoldersAPI({
                data: { id: selectedItemId },
            }),
        onSuccess: (response) => {
            popup('success', response.message);
            setFolderToDelete(INITIAL_STATE);
            setIsDeleteModalOpen(false);
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.FOLDERS, accountId],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.MY_DELETED_DOCUMENTS, accountId],
                exact: false,
            });
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const deleteFolderHandler = ({ selectedItemId }) => {
        deleteMutation.mutate(selectedItemId);
    };

    return (
        <>
            <AdminPageHeader
                heading={MY_FOLDERS.PAGE_HEADING}
                showAddButton
                buttonText={MY_FOLDERS.CREATE_NEW}
                showSearchBar={true}
                searchBarPlaceholder={MY_FOLDERS.SEARCH_BAR_PLACEHOLDER}
                setSearch={setSearch}
                count={pagination.dataCount}
                setCurrentPage={pagination.setCurrentPage}
                backbutton
                handleAddClick={() => {
                    setShowAddModal(true);
                }}
                AddModal={
                    showAddModal ? (
                        <EditFileNameModal
                            showEditModal={showAddModal}
                            setShowEditModal={setShowAddModal}
                            currentFileName={currentFolder.name ?? ''}
                            onSubmit={(v) =>
                                currentFolder?.name
                                    ? renameFolderHandler(v)
                                    : createFolderHandler(v)
                            }
                            modalTitle={
                                currentFolder.name ? MY_FOLDERS.RENAME : MY_FOLDERS.CREATE_NEW
                            }
                            inputLabel='Folder Name'
                            inputPlaceholder='Name....'
                            onClose={() => setCurrentFolder(INITIAL_STATE)}
                            isLoading={renameMutation.isPending || createMutation.isPending}
                        />
                    ) : null
                }
                headerButtonChildren={
                    width > 590 ? (
                        <button
                            className='ssc-secondary-danger-btn position-relative'
                            type='button'
                            title={MY_DOCUMENTS.RECYCLED_DOCUMENTS}
                            aria-label={MY_DOCUMENTS.RECYCLED_DOCUMENTS}
                            onClick={() => setShowModal(true)}
                        >
                            <Delete stroke='#ff004d' />
                            <p className='ms-1'>{MY_DOCUMENTS.RECYCLED_DOCUMENTS}</p>
                        </button>
                    ) : (
                        <button
                            type='button'
                            id='action-btn-red'
                            title={MY_DOCUMENTS.RECYCLED_DOCUMENTS}
                            aria-label={MY_DOCUMENTS.RECYCLED_DOCUMENTS}
                            onClick={() => setShowModal(true)}
                        >
                            <Delete stroke='#ff004d' />
                        </button>
                    )
                }
            />
            {isFetching ? (
                <CircleSpinner absolute size='3rem' />
            ) : response.data.length > 0 ? (
                <>
                    <div className='d-flex align-items-center justify-content-between'>
                        <span className='folder-pagination'>{`Total ${response?.count} ${response?.count > 1 ? 'Folders' : 'Folder'}`}</span>
                        <span className='folder-pagination'>{`Total ${response?.data?.reduce((acc, curr) => acc + curr.document_count, 0)} File(s)`}</span>
                    </div>
                    <div className='folder-view-main'>
                        {response?.data?.map((folder) => {
                            return (
                                <div
                                    key={folder?.id}
                                    className='folder-container cursor-pointer position-relative'
                                    onClick={() =>
                                        navigate(ROUTES.CUSTOMER.MY_DOCUMENTS_VIEW, {
                                            state: {
                                                folderName: folder?.name,
                                                folderId: folder?.id,
                                                accountId: accountId,
                                            },
                                        })
                                    }
                                >
                                    <span
                                        title={
                                            folder?.document_count > 0
                                                ? `Folder contains ${folder?.document_count} ${folder?.document_count === 1 ? 'file' : 'files'}`
                                                : `Folder is Empty`
                                        }
                                    >
                                        <FolderIcon
                                            size={160}
                                            // className={`folder-icon ${folder?.document_count === 0 ? 'no-files' : null}`}
                                            className={`folder-icon`}
                                        />
                                    </span>
                                    <span className='folder-name' title={folder?.name}>
                                        {folder?.name}
                                    </span>
                                    <span
                                        className='position-absolute end-0 d-flex justify-content-end align-items-start'
                                        style={{ top: 7, height: 30, width: 30 }}
                                    >
                                        <span className='d-flex gap-3'>
                                            <span
                                                title='Delete'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    setFolderToDelete({
                                                        id: folder?.id,
                                                        name: folder?.name,
                                                        document_count: folder?.document_count,
                                                    });
                                                    setIsDeleteModalOpen(true);
                                                }}
                                            >
                                                <Delete width={16} height={16} />
                                            </span>
                                            <span
                                                title='Rename'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    setCurrentFolder({
                                                        name: folder.name,
                                                        id: folder.id,
                                                    });
                                                    setShowAddModal(true);
                                                }}
                                            >
                                                <Edit size={16} />
                                            </span>
                                        </span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <main className='empty-folders'>
                    <span className='d-flex flex-column gap-3 align-items-center'>
                        <button
                            type='button'
                            id='action-btn-green'
                            title={MY_FOLDERS.CREATE_NEW}
                            onClick={() => setShowAddModal(true)}
                        >
                            <IconPlus height={44} width={44} />
                        </button>
                        <span style={{ fontSize: 22, color: `var(--col-body)` }}>
                            No Folders Found
                        </span>
                    </span>
                </main>
            )}
            {isDeleteModalOpen && (
                <DeleteConfirmationModal
                    isDeleteModal={isDeleteModalOpen}
                    setIsDeleteModal={setIsDeleteModalOpen}
                    handleDeleteClick={deleteFolderHandler}
                    selectedItemId={folderToDelete?.id}
                    deleteButtonName={folderToDelete?.name}
                    extraWarningText={
                        folderToDelete.document_count === 0
                            ? null
                            : `This will also move ${folderToDelete?.document_count} folder's ${folderToDelete?.document_count > 1 ? 'files' : 'file'} to RECYCLE BIN`
                    }
                    isLoading={deleteMutation.isPending}
                />
            )}
            {showModal && (
                <DeletedDocumentsModal
                    showModal={showModal}
                    setShowModal={setShowModal}
                    response={response}
                    setResponse={setResponse}
                    searchBarPlaceholder={MY_DOCUMENTS.SEARCH_BAR_PLACEHOLDER}
                    pageName={MY_DOCUMENTS.RECYCLED_DOCUMENTS}
                />
            )}
        </>
    );
};
